import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useSubscription } from "../context/SubscriptionContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Switch } from "../components/ui/switch";
// Phase 19.24 — reuse AvatarUploadDialog in business-logo mode
import { AvatarUploadDialog } from "../components/AvatarUploadDialog";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Loader2,
  Plus,
  Trash2,
  Save,
  Building2,
  CreditCard,
  Shield,
  MapPin,
  Users,
  FileText,
  DollarSign,
  Eye,
  EyeOff,
  Settings2,
  Pencil,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BusinessProfile = () => {
  const { token, isOfficeManager } = useAuth();
  const { hasFeature } = useSubscription();
  const payrollActive = hasFeature("payroll");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  
  // Visibility toggles
  const [showBanking, setShowBanking] = useState(false);
  
  // Dialogs
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [bankDialogOpen, setBankDialogOpen] = useState(false);
  const [insuranceDialogOpen, setInsuranceDialogOpen] = useState(false);
  const [customSectionDialogOpen, setCustomSectionDialogOpen] = useState(false);
  const [customFieldDialogOpen, setCustomFieldDialogOpen] = useState(false);
  // Phase 19.24: business logo upload dialog
  const [logoDialogOpen, setLogoDialogOpen] = useState(false);
  
  // Edit indices
  const [editingAddressIndex, setEditingAddressIndex] = useState(null);
  const [editingContactIndex, setEditingContactIndex] = useState(null);
  
  // Custom sections state
  const [editingSection, setEditingSection] = useState(null);
  const [editingSectionIndex, setEditingSectionIndex] = useState(null);
  const [newSection, setNewSection] = useState({ name: "", description: "", fields: [] });
  const [newField, setNewField] = useState({ label: "", value: "", type: "text" });
  const [currentSectionIndex, setCurrentSectionIndex] = useState(null);
  
  // Form data
  const [newAddress, setNewAddress] = useState({
    type: "main",
    street: "",
    city: "",
    province: "British Columbia",
    postal_code: "",
    country: "Canada",
    is_primary: false
  });
  
  const [newContact, setNewContact] = useState({
    name: "",
    title: "",
    phone: "",
    email: "",
    is_primary: false
  });
  
  const [newBank, setNewBank] = useState({
    name: "",
    bank_name: "",
    institution_number: "",
    transit_number: "",
    account_number: "",
    account_type: "chequing",
    use_for_payroll: false,
    use_for_receivables: false,
    use_for_payables: false,
    is_primary: false
  });
  
  const [newInsurance, setNewInsurance] = useState({
    type: "liability",
    provider: "",
    policy_number: "",
    coverage_amount: "",
    premium: "",
    start_date: "",
    expiry_date: "",
    notes: ""
  });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/business/profile`, { headers });
      setProfile(res.data);
    } catch (error) {
      toast.error("Failed to load business profile");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/business/profile`, profile, { headers });
      toast.success("Business profile saved");
    } catch (error) {
      toast.error("Failed to save business profile");
    } finally {
      setSaving(false);
    }
  };

  const handleAddAddress = () => {
    if (editingAddressIndex !== null) {
      setProfile(prev => ({
        ...prev,
        addresses: prev.addresses.map((addr, i) => i === editingAddressIndex ? { ...addr, ...newAddress } : addr)
      }));
      setEditingAddressIndex(null);
    } else {
      setProfile(prev => ({
        ...prev,
        addresses: [...(prev.addresses || []), { id: Date.now().toString(), ...newAddress }]
      }));
    }
    setAddressDialogOpen(false);
    setNewAddress({
      type: "main",
      street: "",
      city: "",
      province: "British Columbia",
      postal_code: "",
      country: "Canada",
      is_primary: false
    });
  };

  const handleEditAddress = (index) => {
    const addr = profile.addresses[index];
    setNewAddress({ ...addr });
    setEditingAddressIndex(index);
    setAddressDialogOpen(true);
  };

  const handleDeleteAddress = (index) => {
    setProfile(prev => ({
      ...prev,
      addresses: prev.addresses.filter((_, i) => i !== index)
    }));
  };

  const handleAddContact = () => {
    if (editingContactIndex !== null) {
      setProfile(prev => ({
        ...prev,
        contacts: prev.contacts.map((c, i) => i === editingContactIndex ? { ...c, ...newContact } : c)
      }));
      setEditingContactIndex(null);
    } else {
      setProfile(prev => ({
        ...prev,
        contacts: [...(prev.contacts || []), { id: Date.now().toString(), ...newContact }]
      }));
    }
    setContactDialogOpen(false);
    setNewContact({
      name: "",
      title: "",
      phone: "",
      email: "",
      is_primary: false
    });
  };

  const handleEditContact = (index) => {
    const contact = profile.contacts[index];
    setNewContact({ ...contact });
    setEditingContactIndex(index);
    setContactDialogOpen(true);
  };

  const handleDeleteContact = (index) => {
    setProfile(prev => ({
      ...prev,
      contacts: prev.contacts.filter((_, i) => i !== index)
    }));
  };

  const handleAddBank = async () => {
    try {
      const res = await axios.post(`${API}/business/bank-accounts`, newBank, { headers });
      setProfile(prev => ({
        ...prev,
        bank_accounts: [...(prev.bank_accounts || []), res.data]
      }));
      setBankDialogOpen(false);
      setNewBank({
        name: "",
        bank_name: "",
        institution_number: "",
        transit_number: "",
        account_number: "",
        account_type: "chequing",
        use_for_payroll: false,
        use_for_receivables: false,
        use_for_payables: false,
        is_primary: false
      });
      toast.success("Bank account added");
    } catch (error) {
      toast.error("Failed to add bank account");
    }
  };

  const handleDeleteBank = async (accountId) => {
    try {
      await axios.delete(`${API}/business/bank-accounts/${accountId}`, { headers });
      setProfile(prev => ({
        ...prev,
        bank_accounts: prev.bank_accounts.filter(b => b.id !== accountId)
      }));
      toast.success("Bank account removed");
    } catch (error) {
      toast.error("Failed to remove bank account");
    }
  };

  const handleAddInsurance = async () => {
    try {
      const payload = {
        ...newInsurance,
        coverage_amount: newInsurance.coverage_amount ? parseFloat(newInsurance.coverage_amount) : null,
        premium: newInsurance.premium ? parseFloat(newInsurance.premium) : null,
        start_date: newInsurance.start_date || null,
        expiry_date: newInsurance.expiry_date || null,
      };
      const res = await axios.post(`${API}/business/insurance`, payload, { headers });
      setProfile(prev => ({
        ...prev,
        insurance_policies: [...(prev.insurance_policies || []), res.data]
      }));
      setInsuranceDialogOpen(false);
      setNewInsurance({
        type: "liability",
        provider: "",
        policy_number: "",
        coverage_amount: "",
        premium: "",
        start_date: "",
        expiry_date: "",
        notes: ""
      });
      toast.success("Insurance policy added");
    } catch (error) {
      toast.error("Failed to add insurance policy");
    }
  };

  const handleDeleteInsurance = async (policyId) => {
    try {
      await axios.delete(`${API}/business/insurance/${policyId}`, { headers });
      setProfile(prev => ({
        ...prev,
        insurance_policies: prev.insurance_policies.filter(p => p.id !== policyId)
      }));
      toast.success("Insurance policy removed");
    } catch (error) {
      toast.error("Failed to remove insurance policy");
    }
  };

  // Custom Sections Handlers
  const handleOpenCustomSectionDialog = (section = null, index = null) => {
    if (section) {
      setEditingSection(section);
      setEditingSectionIndex(index);
      setNewSection({ ...section });
    } else {
      setEditingSection(null);
      setEditingSectionIndex(null);
      setNewSection({ name: "", description: "", fields: [] });
    }
    setCustomSectionDialogOpen(true);
  };

  const handleSaveCustomSection = () => {
    if (!newSection.name) {
      toast.error("Section name is required");
      return;
    }
    
    const customSections = [...(profile.custom_sections || [])];
    
    if (editingSectionIndex !== null) {
      // Update existing section
      customSections[editingSectionIndex] = { ...newSection, id: editingSection.id || Date.now().toString() };
    } else {
      // Add new section
      customSections.push({ ...newSection, id: Date.now().toString() });
    }
    
    setProfile(prev => ({ ...prev, custom_sections: customSections }));
    setCustomSectionDialogOpen(false);
    setNewSection({ name: "", description: "", fields: [] });
    setEditingSection(null);
    setEditingSectionIndex(null);
    toast.success(editingSectionIndex !== null ? "Section updated" : "Section added");
  };

  const handleDeleteCustomSection = (index) => {
    if (!window.confirm("Delete this custom section and all its fields?")) return;
    const customSections = [...(profile.custom_sections || [])];
    customSections.splice(index, 1);
    setProfile(prev => ({ ...prev, custom_sections: customSections }));
    toast.success("Section deleted");
  };

  const handleOpenFieldDialog = (sectionIndex) => {
    setCurrentSectionIndex(sectionIndex);
    setNewField({ label: "", value: "", type: "text" });
    setCustomFieldDialogOpen(true);
  };

  const handleAddField = () => {
    if (!newField.label) {
      toast.error("Field label is required");
      return;
    }
    
    const customSections = [...(profile.custom_sections || [])];
    const section = { ...customSections[currentSectionIndex] };
    section.fields = [...(section.fields || []), { ...newField, id: Date.now().toString() }];
    customSections[currentSectionIndex] = section;
    
    setProfile(prev => ({ ...prev, custom_sections: customSections }));
    setCustomFieldDialogOpen(false);
    setNewField({ label: "", value: "", type: "text" });
    toast.success("Field added");
  };

  const handleUpdateFieldValue = (sectionIndex, fieldIndex, value) => {
    const customSections = [...(profile.custom_sections || [])];
    const section = { ...customSections[sectionIndex] };
    section.fields = [...section.fields];
    section.fields[fieldIndex] = { ...section.fields[fieldIndex], value };
    customSections[sectionIndex] = section;
    setProfile(prev => ({ ...prev, custom_sections: customSections }));
  };

  const handleDeleteField = (sectionIndex, fieldIndex) => {
    const customSections = [...(profile.custom_sections || [])];
    const section = { ...customSections[sectionIndex] };
    section.fields = section.fields.filter((_, i) => i !== fieldIndex);
    customSections[sectionIndex] = section;
    setProfile(prev => ({ ...prev, custom_sections: customSections }));
    toast.success("Field removed");
  };

  const maskValue = (value) => {
    if (!value) return "";
    if (showBanking) return value;
    return "•".repeat(value.length);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="business-profile-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Business Profile</h1>
          <p className="text-muted-foreground">
            Manage your company information and settings
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save All Changes
        </Button>
      </div>

      <Tabs defaultValue="general" className="w-full">
        <TabsList className="w-full flex flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="general" className="flex-1 min-w-[80px] flex items-center justify-center gap-1">
            <Building2 className="h-4 w-4 hidden sm:block" />
            General
          </TabsTrigger>
          <TabsTrigger value="tax" className="flex-1 min-w-[80px] flex items-center justify-center gap-1">
            <DollarSign className="h-4 w-4 hidden sm:block" />
            Tax
          </TabsTrigger>
          <TabsTrigger value="addresses" className="flex-1 min-w-[80px] flex items-center justify-center gap-1">
            <MapPin className="h-4 w-4 hidden sm:block" />
            Addresses
          </TabsTrigger>
          <TabsTrigger value="contacts" className="flex-1 min-w-[80px] flex items-center justify-center gap-1">
            <Users className="h-4 w-4 hidden sm:block" />
            Contacts
          </TabsTrigger>
          <TabsTrigger value="banking" className="flex-1 min-w-[80px] flex items-center justify-center gap-1">
            <CreditCard className="h-4 w-4 hidden sm:block" />
            Banking
          </TabsTrigger>
          <TabsTrigger value="insurance" className="flex-1 min-w-[80px] flex items-center justify-center gap-1">
            <Shield className="h-4 w-4 hidden sm:block" />
            Insurance
          </TabsTrigger>
          <TabsTrigger value="custom" className="flex-1 min-w-[80px] flex items-center justify-center gap-1">
            <Settings2 className="h-4 w-4 hidden sm:block" />
            Custom
          </TabsTrigger>
        </TabsList>

        {/* General Tab */}
        <TabsContent value="general" className="space-y-4">
          {/* Phase 19.24 — Business Logo */}
          <Card data-testid="business-logo-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Business Logo
              </CardTitle>
              <CardDescription>
                Shown on PDFs/emails. Optionally cascade to your Employee &amp; Client portals.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-lg bg-muted flex items-center justify-center overflow-hidden border">
                {profile?.logo_url ? (
                  <img
                    src={
                      profile.logo_url.startsWith('http')
                        ? profile.logo_url
                        : `${process.env.REACT_APP_BACKEND_URL}${profile.logo_url}?auth=${localStorage.getItem('token')}`
                    }
                    alt="Business logo"
                    className="w-full h-full object-contain"
                    data-testid="business-logo-preview"
                  />
                ) : (
                  <Building2 className="w-8 h-8 text-muted-foreground/40" />
                )}
              </div>
              <div className="flex-1">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setLogoDialogOpen(true)}
                  data-testid="business-logo-upload-btn"
                >
                  {profile?.logo_url ? 'Change Logo' : 'Upload Logo'}
                </Button>
                {profile?.apply_logo_to_portals && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Currently shown next to "Business Genie" in your Employee &amp; Client portals.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Business Information</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Legal Name</Label>
                <Input
                  value={profile?.legal_name || ""}
                  onChange={(e) => setProfile({ ...profile, legal_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Operating Name (DBA)</Label>
                <Input
                  value={profile?.operating_name || ""}
                  onChange={(e) => setProfile({ ...profile, operating_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Business Type</Label>
                <Select
                  value={profile?.business_type || "corporation"}
                  onValueChange={(value) => setProfile({ ...profile, business_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sole_proprietor">Sole Proprietor</SelectItem>
                    <SelectItem value="partnership">Partnership</SelectItem>
                    <SelectItem value="corporation">Corporation</SelectItem>
                    <SelectItem value="llc">LLC</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Incorporation Date</Label>
                <Input
                  type="date"
                  value={profile?.incorporation_date || ""}
                  onChange={(e) => setProfile({ ...profile, incorporation_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Main Phone</Label>
                <Input
                  value={profile?.main_phone || ""}
                  onChange={(e) => setProfile({ ...profile, main_phone: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Secondary Phone</Label>
                <Input
                  value={profile?.secondary_phone || ""}
                  onChange={(e) => setProfile({ ...profile, secondary_phone: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Fax</Label>
                <Input
                  value={profile?.fax || ""}
                  onChange={(e) => setProfile({ ...profile, fax: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={profile?.email || ""}
                  onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Website</Label>
                <Input
                  value={profile?.website || ""}
                  onChange={(e) => setProfile({ ...profile, website: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Fiscal Year End (MM-DD)</Label>
                <Input
                  value={profile?.fiscal_year_end || "12-31"}
                  onChange={(e) => setProfile({ ...profile, fiscal_year_end: e.target.value })}
                  placeholder="12-31"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Tax Information</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>CRA Business Number</Label>
                <Input
                  value={profile?.business_number || ""}
                  onChange={(e) => setProfile({ ...profile, business_number: e.target.value })}
                  placeholder="123456789RC0001"
                />
              </div>
              <div className="space-y-2">
                <Label>GST/HST Number</Label>
                <Input
                  value={profile?.gst_hst_number || ""}
                  onChange={(e) => setProfile({ ...profile, gst_hst_number: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>PST Number</Label>
                <Input
                  value={profile?.pst_number || ""}
                  onChange={(e) => setProfile({ ...profile, pst_number: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>WCB Number</Label>
                <Input
                  value={profile?.wcb_number || ""}
                  onChange={(e) => setProfile({ ...profile, wcb_number: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Default Tax Province</Label>
                <Select
                  value={profile?.default_tax_province || "British Columbia"}
                  onValueChange={(value) => setProfile({ ...profile, default_tax_province: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="British Columbia">British Columbia</SelectItem>
                    <SelectItem value="Alberta">Alberta</SelectItem>
                    <SelectItem value="Ontario">Ontario</SelectItem>
                    <SelectItem value="Quebec">Quebec</SelectItem>
                    <SelectItem value="Manitoba">Manitoba</SelectItem>
                    <SelectItem value="Saskatchewan">Saskatchewan</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card className={!payrollActive ? "opacity-60" : ""}>
            <CardHeader>
              <CardTitle>Payroll Settings</CardTitle>
              {!payrollActive && <p className="text-xs text-muted-foreground">Payroll module is not active. Subscribe to enable.</p>}
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Payroll Frequency</Label>
                <Select
                  value={profile?.payroll_frequency || "biweekly"}
                  onValueChange={(value) => setProfile({ ...profile, payroll_frequency: value })}
                  disabled={!payrollActive}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="biweekly">Bi-Weekly</SelectItem>
                    <SelectItem value="bimonthly">Bi-Monthly (1st & 15th)</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Processing Mode</Label>
                <Select
                  value={profile?.payroll_processing_mode || "internal"}
                  onValueChange={(value) => setProfile({ ...profile, payroll_processing_mode: value })}
                  disabled={!payrollActive}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="internal">Internal Tracking (Reports Only)</SelectItem>
                    <SelectItem value="integrated">Integrated Processing (Premium)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tax Settings Tab */}
        <TabsContent value="tax" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Tax Configuration</CardTitle>
              <CardDescription>
                Set up default tax rates and country/province settings
              </CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Default Country</Label>
                <Select
                  value={profile?.default_tax_country || "Canada"}
                  onValueChange={(value) => {
                    const currencyMap = { Canada: "CAD", "United States": "USD", "United Kingdom": "GBP", Australia: "AUD" };
                    setProfile({ ...profile, default_tax_country: value, default_currency: currencyMap[value] || "USD" });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Canada">Canada</SelectItem>
                    <SelectItem value="United States">United States</SelectItem>
                    <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                    <SelectItem value="Australia">Australia</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{profile?.default_tax_country === "United States" ? "State" : profile?.default_tax_country === "United Kingdom" ? "Region" : "Province/State"}</Label>
                <Select
                  value={profile?.default_tax_province || "British Columbia"}
                  onValueChange={(value) => setProfile({ ...profile, default_tax_province: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(profile?.default_tax_country || "Canada") === "Canada" && (
                      <>
                        <SelectItem value="Alberta">Alberta</SelectItem>
                        <SelectItem value="British Columbia">British Columbia</SelectItem>
                        <SelectItem value="Manitoba">Manitoba</SelectItem>
                        <SelectItem value="New Brunswick">New Brunswick</SelectItem>
                        <SelectItem value="Newfoundland and Labrador">Newfoundland and Labrador</SelectItem>
                        <SelectItem value="Nova Scotia">Nova Scotia</SelectItem>
                        <SelectItem value="Ontario">Ontario</SelectItem>
                        <SelectItem value="Prince Edward Island">Prince Edward Island</SelectItem>
                        <SelectItem value="Quebec">Quebec</SelectItem>
                        <SelectItem value="Saskatchewan">Saskatchewan</SelectItem>
                      </>
                    )}
                    {profile?.default_tax_country === "United States" && (
                      <>
                        <SelectItem value="California">California</SelectItem>
                        <SelectItem value="Florida">Florida</SelectItem>
                        <SelectItem value="Illinois">Illinois</SelectItem>
                        <SelectItem value="New York">New York</SelectItem>
                        <SelectItem value="Texas">Texas</SelectItem>
                        <SelectItem value="Washington">Washington</SelectItem>
                      </>
                    )}
                    {profile?.default_tax_country === "United Kingdom" && (
                      <>
                        <SelectItem value="England">England</SelectItem>
                        <SelectItem value="Scotland">Scotland</SelectItem>
                        <SelectItem value="Wales">Wales</SelectItem>
                        <SelectItem value="Northern Ireland">Northern Ireland</SelectItem>
                      </>
                    )}
                    {profile?.default_tax_country === "Australia" && (
                      <>
                        <SelectItem value="New South Wales">New South Wales</SelectItem>
                        <SelectItem value="Victoria">Victoria</SelectItem>
                        <SelectItem value="Queensland">Queensland</SelectItem>
                        <SelectItem value="South Australia">South Australia</SelectItem>
                        <SelectItem value="Western Australia">Western Australia</SelectItem>
                        <SelectItem value="Tasmania">Tasmania</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Country-specific tax rates */}
              {(profile?.default_tax_country || "Canada") === "Canada" && (
                <>
                  <div className="space-y-2">
                    <Label>GST Rate (%)</Label>
                    <Input type="number" step="0.01" value={profile?.gst_rate || 5} onChange={(e) => setProfile({ ...profile, gst_rate: parseFloat(e.target.value) || 0 })} />
                  </div>
                  <div className="space-y-2">
                    <Label>PST Rate (%)</Label>
                    <Input type="number" step="0.01" value={profile?.pst_rate || 7} onChange={(e) => setProfile({ ...profile, pst_rate: parseFloat(e.target.value) || 0 })} />
                  </div>
                  <div className="space-y-2">
                    <Label>HST Rate (%) - if applicable</Label>
                    <Input type="number" step="0.01" value={profile?.hst_rate || 0} onChange={(e) => setProfile({ ...profile, hst_rate: parseFloat(e.target.value) || 0 })} />
                  </div>
                </>
              )}
              {profile?.default_tax_country === "United States" && (
                <>
                  <div className="space-y-2">
                    <Label>State Sales Tax Rate (%)</Label>
                    <Input type="number" step="0.01" value={profile?.state_sales_tax_rate || 0} onChange={(e) => setProfile({ ...profile, state_sales_tax_rate: parseFloat(e.target.value) || 0 })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Local Tax Rate (%)</Label>
                    <Input type="number" step="0.01" value={profile?.local_tax_rate || 0} onChange={(e) => setProfile({ ...profile, local_tax_rate: parseFloat(e.target.value) || 0 })} />
                  </div>
                </>
              )}
              {profile?.default_tax_country === "United Kingdom" && (
                <div className="space-y-2">
                  <Label>VAT Rate (%)</Label>
                  <Input type="number" step="0.01" value={profile?.vat_rate || 20} onChange={(e) => setProfile({ ...profile, vat_rate: parseFloat(e.target.value) || 0 })} />
                </div>
              )}
              {profile?.default_tax_country === "Australia" && (
                <div className="space-y-2">
                  <Label>GST Rate (%)</Label>
                  <Input type="number" step="0.01" value={profile?.gst_rate || 10} onChange={(e) => setProfile({ ...profile, gst_rate: parseFloat(e.target.value) || 0 })} />
                </div>
              )}

              <div className="space-y-2">
                <Label>Default Currency</Label>
                <Select
                  value={profile?.default_currency || "CAD"}
                  onValueChange={(value) => setProfile({ ...profile, default_currency: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CAD">CAD - Canadian Dollar</SelectItem>
                    <SelectItem value="USD">USD - US Dollar</SelectItem>
                    <SelectItem value="GBP">GBP - British Pound</SelectItem>
                    <SelectItem value="EUR">EUR - Euro</SelectItem>
                    <SelectItem value="AUD">AUD - Australian Dollar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Tax Registration Numbers</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4">
              {(profile?.default_tax_country || "Canada") === "Canada" && (
                <>
                  <div className="space-y-2">
                    <Label>CRA Business Number</Label>
                    <Input value={profile?.business_number || ""} onChange={(e) => setProfile({ ...profile, business_number: e.target.value })} placeholder="123456789RC0001" />
                  </div>
                  <div className="space-y-2">
                    <Label>GST/HST Registration</Label>
                    <Input value={profile?.gst_hst_number || ""} onChange={(e) => setProfile({ ...profile, gst_hst_number: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>PST Registration</Label>
                    <Input value={profile?.pst_number || ""} onChange={(e) => setProfile({ ...profile, pst_number: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>WCB Number</Label>
                    <Input value={profile?.wcb_number || ""} onChange={(e) => setProfile({ ...profile, wcb_number: e.target.value })} />
                  </div>
                </>
              )}
              {profile?.default_tax_country === "United States" && (
                <>
                  <div className="space-y-2">
                    <Label>EIN (Federal Tax ID)</Label>
                    <Input value={profile?.ein_number || ""} onChange={(e) => setProfile({ ...profile, ein_number: e.target.value })} placeholder="XX-XXXXXXX" />
                  </div>
                  <div className="space-y-2">
                    <Label>State Tax ID</Label>
                    <Input value={profile?.state_tax_id || ""} onChange={(e) => setProfile({ ...profile, state_tax_id: e.target.value })} />
                  </div>
                </>
              )}
              {profile?.default_tax_country === "United Kingdom" && (
                <>
                  <div className="space-y-2">
                    <Label>VAT Registration Number</Label>
                    <Input value={profile?.vat_number || ""} onChange={(e) => setProfile({ ...profile, vat_number: e.target.value })} placeholder="GB123456789" />
                  </div>
                  <div className="space-y-2">
                    <Label>Company Registration Number</Label>
                    <Input value={profile?.company_reg_number || ""} onChange={(e) => setProfile({ ...profile, company_reg_number: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>UTR Number</Label>
                    <Input value={profile?.utr_number || ""} onChange={(e) => setProfile({ ...profile, utr_number: e.target.value })} />
                  </div>
                </>
              )}
              {profile?.default_tax_country === "Australia" && (
                <>
                  <div className="space-y-2">
                    <Label>ABN (Australian Business Number)</Label>
                    <Input value={profile?.abn_number || ""} onChange={(e) => setProfile({ ...profile, abn_number: e.target.value })} placeholder="XX XXX XXX XXX" />
                  </div>
                  <div className="space-y-2">
                    <Label>ACN (Australian Company Number)</Label>
                    <Input value={profile?.acn_number || ""} onChange={(e) => setProfile({ ...profile, acn_number: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>GST Registration</Label>
                    <Input value={profile?.gst_registration || ""} onChange={(e) => setProfile({ ...profile, gst_registration: e.target.value })} />
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Addresses Tab */}
        <TabsContent value="addresses" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Business Addresses</CardTitle>
              <Button size="sm" onClick={() => setAddressDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-1" /> Add Address
              </Button>
            </CardHeader>
            <CardContent>
              {(profile?.addresses || []).length === 0 ? (
                <p className="text-muted-foreground text-sm">No addresses added</p>
              ) : (
                <div className="space-y-4">
                  {profile.addresses.map((addr, index) => (
                    <div key={addr.id || index} className="p-4 border rounded-lg flex justify-between items-start">
                      <div>
                        <p className="font-medium capitalize">{addr.type} Address {addr.is_primary && "(Primary)"}</p>
                        <p className="text-sm text-muted-foreground">{addr.street}</p>
                        <p className="text-sm text-muted-foreground">
                          {addr.city}, {addr.province} {addr.postal_code}
                        </p>
                        <p className="text-sm text-muted-foreground">{addr.country}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleEditAddress(index)} data-testid={`edit-address-${index}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteAddress(index)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contacts Tab */}
        <TabsContent value="contacts" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Key Contacts</CardTitle>
              <Button size="sm" onClick={() => setContactDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-1" /> Add Contact
              </Button>
            </CardHeader>
            <CardContent>
              {(profile?.contacts || []).length === 0 ? (
                <p className="text-muted-foreground text-sm">No contacts added</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Primary</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {profile.contacts.map((contact, index) => (
                      <TableRow key={contact.id || index}>
                        <TableCell className="font-medium">{contact.name}</TableCell>
                        <TableCell>{contact.title}</TableCell>
                        <TableCell>{contact.phone}</TableCell>
                        <TableCell>{contact.email}</TableCell>
                        <TableCell>{contact.is_primary ? "Yes" : "No"}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" onClick={() => handleEditContact(index)} data-testid={`edit-contact-${index}`}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDeleteContact(index)}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Banking Tab */}
        <TabsContent value="banking" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Bank Accounts</CardTitle>
                <CardDescription>Manage business bank accounts for payroll, receivables, and payables</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowBanking(!showBanking)}>
                  {showBanking ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
                  {showBanking ? "Hide" : "Show"} Details
                </Button>
                <Button size="sm" onClick={() => setBankDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-1" /> Add Account
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {(profile?.bank_accounts || []).length === 0 ? (
                <p className="text-muted-foreground text-sm">No bank accounts added</p>
              ) : (
                <div className="space-y-4">
                  {profile.bank_accounts.map((bank) => (
                    <div key={bank.id} className="p-4 border rounded-lg flex justify-between items-start">
                      <div className="space-y-1">
                        <p className="font-medium">{bank.name} {bank.is_primary && "(Primary)"}</p>
                        <p className="text-sm text-muted-foreground">{bank.bank_name}</p>
                        <p className="text-sm font-mono">
                          {showBanking ? `${bank.institution_number}-${bank.transit_number}-${bank.account_number}` : "•••-•••••-••••••••"}
                        </p>
                        <div className="flex gap-2 mt-2">
                          {bank.use_for_payroll && <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Payroll</span>}
                          {bank.use_for_receivables && <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Receivables</span>}
                          {bank.use_for_payables && <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded">Payables</span>}
                        </div>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteBank(bank.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Insurance Tab */}
        <TabsContent value="insurance" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Insurance Policies</CardTitle>
              <Button size="sm" onClick={() => setInsuranceDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-1" /> Add Policy
              </Button>
            </CardHeader>
            <CardContent>
              {(profile?.insurance_policies || []).length === 0 ? (
                <p className="text-muted-foreground text-sm">No insurance policies added</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Provider</TableHead>
                      <TableHead>Policy #</TableHead>
                      <TableHead>Coverage</TableHead>
                      <TableHead>Expiry</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {profile.insurance_policies.map((policy) => (
                      <TableRow key={policy.id}>
                        <TableCell className="font-medium capitalize">{policy.type?.replace(/_/g, " ")}</TableCell>
                        <TableCell>{policy.provider}</TableCell>
                        <TableCell>{policy.policy_number}</TableCell>
                        <TableCell>${policy.coverage_amount?.toLocaleString()}</TableCell>
                        <TableCell>{policy.expiry_date}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteInsurance(policy.id)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Custom Sections Tab */}
        <TabsContent value="custom" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Custom Information Sections</CardTitle>
                <CardDescription>Create custom sections to store additional business information</CardDescription>
              </div>
              <Button size="sm" onClick={() => handleOpenCustomSectionDialog()}>
                <Plus className="h-4 w-4 mr-1" /> Add Section
              </Button>
            </CardHeader>
            <CardContent>
              {(profile?.custom_sections || []).length === 0 ? (
                <div className="text-center py-8">
                  <Settings2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No custom sections yet</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Create sections like "Insurance Details", "Certifications", "Vendor Info", etc.
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {profile.custom_sections.map((section, sectionIndex) => (
                    <Card key={section.id} className="border-dashed">
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <CardTitle className="text-lg">{section.name}</CardTitle>
                            {section.description && (
                              <CardDescription>{section.description}</CardDescription>
                            )}
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenFieldDialog(sectionIndex)}
                              title="Add Field"
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenCustomSectionDialog(section, sectionIndex)}
                              title="Edit Section"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteCustomSection(sectionIndex)}
                              title="Delete Section"
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        {(section.fields || []).length === 0 ? (
                          <p className="text-sm text-muted-foreground">No fields added yet. Click + to add fields.</p>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {section.fields.map((field, fieldIndex) => (
                              <div key={field.id} className="space-y-1">
                                <div className="flex items-center justify-between">
                                  <Label className="text-sm">{field.label}</Label>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={() => handleDeleteField(sectionIndex, fieldIndex)}
                                  >
                                    <Trash2 className="h-3 w-3 text-muted-foreground" />
                                  </Button>
                                </div>
                                {field.type === "textarea" ? (
                                  <Textarea
                                    value={field.value || ""}
                                    onChange={(e) => handleUpdateFieldValue(sectionIndex, fieldIndex, e.target.value)}
                                    rows={2}
                                  />
                                ) : field.type === "date" ? (
                                  <Input
                                    type="date"
                                    value={field.value || ""}
                                    onChange={(e) => handleUpdateFieldValue(sectionIndex, fieldIndex, e.target.value)}
                                  />
                                ) : field.type === "number" ? (
                                  <Input
                                    type="number"
                                    value={field.value || ""}
                                    onChange={(e) => handleUpdateFieldValue(sectionIndex, fieldIndex, e.target.value)}
                                  />
                                ) : (
                                  <Input
                                    value={field.value || ""}
                                    onChange={(e) => handleUpdateFieldValue(sectionIndex, fieldIndex, e.target.value)}
                                  />
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Address Dialog */}
      <Dialog open={addressDialogOpen} onOpenChange={(open) => {
        setAddressDialogOpen(open);
        if (!open) setEditingAddressIndex(null);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingAddressIndex !== null ? "Edit Address" : "Add Address"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select
                  value={newAddress.type}
                  onValueChange={(value) => setNewAddress({ ...newAddress, type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="main">Main</SelectItem>
                    <SelectItem value="mailing">Mailing</SelectItem>
                    <SelectItem value="branch">Branch</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Country</Label>
                <Select
                  value={newAddress.country}
                  onValueChange={(value) => {
                    const defaults = {
                      Canada: { province: "British Columbia", postal_code: "" },
                      "United States": { province: "California", postal_code: "" },
                      "United Kingdom": { province: "England", postal_code: "" },
                      Australia: { province: "New South Wales", postal_code: "" },
                    };
                    setNewAddress({ ...newAddress, country: value, ...(defaults[value] || {}) });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Canada">Canada</SelectItem>
                    <SelectItem value="United States">United States</SelectItem>
                    <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                    <SelectItem value="Australia">Australia</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                checked={newAddress.is_primary}
                onCheckedChange={(checked) => setNewAddress({ ...newAddress, is_primary: checked })}
              />
              <Label>Primary Address</Label>
            </div>
            <div className="space-y-2">
              <Label>Street Address</Label>
              <Input
                value={newAddress.street}
                onChange={(e) => setNewAddress({ ...newAddress, street: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>City</Label>
                <Input
                  value={newAddress.city}
                  onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>{newAddress.country === "United States" ? "State" : newAddress.country === "United Kingdom" ? "Region" : "Province/State"}</Label>
                <Select
                  value={newAddress.province}
                  onValueChange={(value) => setNewAddress({ ...newAddress, province: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {newAddress.country === "Canada" && (
                      <>
                        <SelectItem value="Alberta">Alberta</SelectItem>
                        <SelectItem value="British Columbia">British Columbia</SelectItem>
                        <SelectItem value="Manitoba">Manitoba</SelectItem>
                        <SelectItem value="New Brunswick">New Brunswick</SelectItem>
                        <SelectItem value="Newfoundland and Labrador">Newfoundland and Labrador</SelectItem>
                        <SelectItem value="Nova Scotia">Nova Scotia</SelectItem>
                        <SelectItem value="Ontario">Ontario</SelectItem>
                        <SelectItem value="Prince Edward Island">Prince Edward Island</SelectItem>
                        <SelectItem value="Quebec">Quebec</SelectItem>
                        <SelectItem value="Saskatchewan">Saskatchewan</SelectItem>
                      </>
                    )}
                    {newAddress.country === "United States" && (
                      <>
                        <SelectItem value="Alabama">Alabama</SelectItem>
                        <SelectItem value="Alaska">Alaska</SelectItem>
                        <SelectItem value="Arizona">Arizona</SelectItem>
                        <SelectItem value="California">California</SelectItem>
                        <SelectItem value="Colorado">Colorado</SelectItem>
                        <SelectItem value="Florida">Florida</SelectItem>
                        <SelectItem value="Georgia">Georgia</SelectItem>
                        <SelectItem value="Illinois">Illinois</SelectItem>
                        <SelectItem value="New York">New York</SelectItem>
                        <SelectItem value="Texas">Texas</SelectItem>
                        <SelectItem value="Washington">Washington</SelectItem>
                      </>
                    )}
                    {newAddress.country === "United Kingdom" && (
                      <>
                        <SelectItem value="England">England</SelectItem>
                        <SelectItem value="Scotland">Scotland</SelectItem>
                        <SelectItem value="Wales">Wales</SelectItem>
                        <SelectItem value="Northern Ireland">Northern Ireland</SelectItem>
                      </>
                    )}
                    {newAddress.country === "Australia" && (
                      <>
                        <SelectItem value="New South Wales">New South Wales</SelectItem>
                        <SelectItem value="Victoria">Victoria</SelectItem>
                        <SelectItem value="Queensland">Queensland</SelectItem>
                        <SelectItem value="South Australia">South Australia</SelectItem>
                        <SelectItem value="Western Australia">Western Australia</SelectItem>
                        <SelectItem value="Tasmania">Tasmania</SelectItem>
                        <SelectItem value="ACT">ACT</SelectItem>
                        <SelectItem value="Northern Territory">Northern Territory</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{newAddress.country === "United States" ? "ZIP Code" : newAddress.country === "United Kingdom" ? "Postcode" : "Postal Code"}</Label>
                <Input
                  value={newAddress.postal_code}
                  onChange={(e) => setNewAddress({ ...newAddress, postal_code: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAddressDialogOpen(false); setEditingAddressIndex(null); }}>Cancel</Button>
            <Button onClick={handleAddAddress}>{editingAddressIndex !== null ? "Update Address" : "Add Address"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contact Dialog */}
      <Dialog open={contactDialogOpen} onOpenChange={(open) => {
        setContactDialogOpen(open);
        if (!open) setEditingContactIndex(null);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingContactIndex !== null ? "Edit Contact" : "Add Contact"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  value={newContact.name}
                  onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={newContact.title}
                  onChange={(e) => setNewContact({ ...newContact, title: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={newContact.phone}
                  onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={newContact.email}
                  onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                checked={newContact.is_primary}
                onCheckedChange={(checked) => setNewContact({ ...newContact, is_primary: checked })}
              />
              <Label>Primary Contact</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setContactDialogOpen(false); setEditingContactIndex(null); }}>Cancel</Button>
            <Button onClick={handleAddContact}>{editingContactIndex !== null ? "Update Contact" : "Add Contact"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bank Account Dialog */}
      <Dialog open={bankDialogOpen} onOpenChange={setBankDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Bank Account</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Account Name</Label>
                <Input
                  value={newBank.name}
                  onChange={(e) => setNewBank({ ...newBank, name: e.target.value })}
                  placeholder="e.g., Operating Account"
                />
              </div>
              <div className="space-y-2">
                <Label>Bank Name</Label>
                <Input
                  value={newBank.bank_name}
                  onChange={(e) => setNewBank({ ...newBank, bank_name: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Institution # (3)</Label>
                <Input
                  value={newBank.institution_number}
                  onChange={(e) => setNewBank({ ...newBank, institution_number: e.target.value })}
                  maxLength={3}
                />
              </div>
              <div className="space-y-2">
                <Label>Transit # (5)</Label>
                <Input
                  value={newBank.transit_number}
                  onChange={(e) => setNewBank({ ...newBank, transit_number: e.target.value })}
                  maxLength={5}
                />
              </div>
              <div className="space-y-2">
                <Label>Account #</Label>
                <Input
                  value={newBank.account_number}
                  onChange={(e) => setNewBank({ ...newBank, account_number: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Account Type</Label>
              <Select
                value={newBank.account_type}
                onValueChange={(value) => setNewBank({ ...newBank, account_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="chequing">Chequing</SelectItem>
                  <SelectItem value="savings">Savings</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Use For:</Label>
              <div className="flex gap-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={newBank.use_for_payroll}
                    onCheckedChange={(checked) => setNewBank({ ...newBank, use_for_payroll: checked })}
                    disabled={!payrollActive}
                  />
                  <Label className={!payrollActive ? "text-muted-foreground" : ""}>Payroll {!payrollActive && "(inactive)"}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={newBank.use_for_receivables}
                    onCheckedChange={(checked) => setNewBank({ ...newBank, use_for_receivables: checked })}
                  />
                  <Label>Receivables</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={newBank.use_for_payables}
                    onCheckedChange={(checked) => setNewBank({ ...newBank, use_for_payables: checked })}
                  />
                  <Label>Payables</Label>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                checked={newBank.is_primary}
                onCheckedChange={(checked) => setNewBank({ ...newBank, is_primary: checked })}
              />
              <Label>Primary Account</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBankDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddBank}>Add Account</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Insurance Dialog */}
      <Dialog open={insuranceDialogOpen} onOpenChange={setInsuranceDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Insurance Policy</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select
                  value={newInsurance.type}
                  onValueChange={(value) => setNewInsurance({ ...newInsurance, type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="liability">General Liability</SelectItem>
                    <SelectItem value="workers_comp">Workers Compensation</SelectItem>
                    <SelectItem value="vehicle">Vehicle</SelectItem>
                    <SelectItem value="equipment">Equipment</SelectItem>
                    <SelectItem value="professional">Professional Liability</SelectItem>
                    <SelectItem value="property">Property</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Provider</Label>
                <Input
                  value={newInsurance.provider}
                  onChange={(e) => setNewInsurance({ ...newInsurance, provider: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Policy Number</Label>
                <Input
                  value={newInsurance.policy_number}
                  onChange={(e) => setNewInsurance({ ...newInsurance, policy_number: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Coverage Amount ($)</Label>
                <Input
                  type="number"
                  value={newInsurance.coverage_amount}
                  onChange={(e) => setNewInsurance({ ...newInsurance, coverage_amount: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={newInsurance.start_date}
                  onChange={(e) => setNewInsurance({ ...newInsurance, start_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Expiry Date</Label>
                <Input
                  type="date"
                  value={newInsurance.expiry_date}
                  onChange={(e) => setNewInsurance({ ...newInsurance, expiry_date: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInsuranceDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddInsurance}>Add Policy</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom Section Dialog */}
      <Dialog open={customSectionDialogOpen} onOpenChange={setCustomSectionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingSection ? "Edit Section" : "Add Custom Section"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Section Name *</Label>
              <Input
                value={newSection.name}
                onChange={(e) => setNewSection({ ...newSection, name: e.target.value })}
                placeholder="e.g., Insurance Details, Certifications, Vendor Info"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={newSection.description}
                onChange={(e) => setNewSection({ ...newSection, description: e.target.value })}
                placeholder="Optional description for this section"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCustomSectionDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveCustomSection}>
              {editingSection ? "Save Changes" : "Create Section"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom Field Dialog */}
      <Dialog open={customFieldDialogOpen} onOpenChange={setCustomFieldDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Field</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Field Label *</Label>
              <Input
                value={newField.label}
                onChange={(e) => setNewField({ ...newField, label: e.target.value })}
                placeholder="e.g., Policy Number, Certificate Name"
              />
            </div>
            <div className="space-y-2">
              <Label>Field Type</Label>
              <Select
                value={newField.type}
                onValueChange={(value) => setNewField({ ...newField, type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">Text</SelectItem>
                  <SelectItem value="number">Number</SelectItem>
                  <SelectItem value="date">Date</SelectItem>
                  <SelectItem value="textarea">Long Text</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Default Value</Label>
              <Input
                value={newField.value}
                onChange={(e) => setNewField({ ...newField, value: e.target.value })}
                placeholder="Optional initial value"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCustomFieldDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddField}>Add Field</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 19.24 — Business Logo Upload Dialog */}
      <AvatarUploadDialog
        open={logoDialogOpen}
        onOpenChange={setLogoDialogOpen}
        mode="business-logo"
        currentUrl={profile?.logo_url || null}
        applyToPortals={Boolean(profile?.apply_logo_to_portals)}
        onUploaded={(resp) => {
          if (resp?.removed) {
            setProfile({ ...profile, logo_url: null, apply_logo_to_portals: false });
          } else if (resp) {
            setProfile({
              ...profile,
              logo_url: resp.logo_url || profile.logo_url,
              apply_logo_to_portals: !!resp.apply_to_portals,
            });
          }
        }}
      />
    </div>
  );
};

export default BusinessProfile;
